import { Alert, Button, Select, Spin } from "antd";
import { SelectProps } from "antd/lib/select";
import React, { FC, useCallback, useEffect, useState } from "react";
import Icon from "../assets/icons/icon";
import _t from "../lang/translate";
import { getApiQuery, pageSizeKey, searchKey } from "../services/urlQueryService";
import { useApi } from "../services/useApi";
import { PaginationedData } from "../types/apiTypes";
import { EnumItemType, IdType } from "../types/appTypes";
import { isValidModalType, ModalTypes } from "../types/modalTypes";
import openModal from "./modal/openModal";
import debounce from "lodash/debounce";
import { RefSelectProps } from "antd/lib/select";

const { Option } = Select;

export interface ServerSelectBoxProps extends SelectProps<[]> {
  apiUrl: string;
  initTitle?: string | null;
  onAddClick?: () => void;
  addModalType?: ModalTypes | null;
  skipItems?: IdType[] | null;
  dark?: boolean;
  onItemAdded?: (option: EnumItemType, updatedValues: IdType[] | IdType) => void;
  value?: any;
  selectedTitle?: string | null;
  addModalExtraValues?: { [x: string]: any };
  filters?: string;
  pageSize?: number | string;
  serverSideFilter?: boolean;
  urlSetDelay?: number;
}

const ServerSelectBox = React.forwardRef<RefSelectProps, ServerSelectBoxProps>(({
  apiUrl,
  value,
  onAddClick,
  onItemAdded,
  addModalExtraValues,
  addModalType,
  skipItems,
  initTitle,
  dark,
  dropdownClassName,
  selectedTitle,
  disabled,
  loading,
  mode,
  filters = "",
  pageSize = "9999",
  serverSideFilter,
  urlSetDelay = 200,
  ...rest
}, ref) => {
  const [{ data: response, isLoading, isError }, setUrl, setData] = useApi<PaginationedData<EnumItemType>>("", {
    data: [],
  });

  const delayedSetUrl = useCallback(
    debounce((url: string) => {
      setUrl(url);
    }, urlSetDelay),
    [setUrl, urlSetDelay]
  );

  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedOption, setSelectedOption] = useState<EnumItemType | null>(null);

  const { data } = response;

  useEffect(() => {
    const query = new URLSearchParams(filters);
    query.set(pageSizeKey, pageSize.toString());
    if (serverSideFilter && searchValue) {
      query.set(searchKey, searchValue)
    }

    const url = apiUrl + getApiQuery(query.toString());
    delayedSetUrl(url);
  }, [filters, delayedSetUrl, apiUrl, pageSize, searchValue, serverSideFilter]);

  const handleAddNewItem = async () => {
    onAddClick && onAddClick();
    if (addModalType && isValidModalType(addModalType)) {
      try {
        const newData = await openModal<EnumItemType>(addModalType, {
          ...addModalExtraValues,
          initialValues: searchValue,
        });

        setData({ data: [...data, newData] });
        if (mode === "multiple" || mode === "tags") {
          onItemAdded &&
            onItemAdded(newData as EnumItemType, Array.isArray(value) ? [...value, String(newData.id)] : [String(newData.id)]);
        } else {
          onItemAdded && onItemAdded(newData as EnumItemType, newData.id);
        }
      } catch (error) {
      } finally {
        setSearchValue("");
      }
    }
  };

  const handleFilter = (input: string, option: any): boolean => {
    const label = option?.label || option?.title;
    return label && label.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
  };

  const handleSelect = (selectedValue: any, option: any) => {
    const id = Number(selectedValue);
    if (!Number.isNaN(id)) {
      setSelectedOption({
        id,
        title: option?.label?.toString() ?? option?.children?.toString() ?? selectedValue.toString(),
      });
    }
    (rest.onSelect as any)?.(selectedValue, option);
  };

  const filteredData = skipItems ? data.filter((e) => !skipItems.includes(e.id)) : data;
  const optionData = selectedOption && !filteredData.some((item) => item.id === selectedOption.id)
    ? [selectedOption, ...filteredData]
    : filteredData;

  return (
    <>
      <Select
        {...rest}
        loading={isLoading || loading}
        disabled={disabled}
        showSearch
        mode={mode}
        dropdownClassName={`${dark ? "dropdown-dark " : ""}${dropdownClassName}`}
        defaultActiveFirstOption={true}
        ref={ref}
        onSearch={setSearchValue}
        onSelect={handleSelect}
        searchValue={searchValue}
        optionLabelProp="label"
        filterOption={serverSideFilter ? false : handleFilter}
        onBlur={(e) => {
          if (e.relatedTarget?.tagName === "BUTTON") {
            e.preventDefault();
          }
        }}
        value={isLoading || value === null ? undefined : value}
        dropdownRender={(menu) => (
          <>
            {menu}
            {((typeof addModalType === "string" && isValidModalType(addModalType)) || onAddClick) && (
              <div className="select-add-btn">
                <Button
                  type="dashed"
                  block
                  htmlType="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }} // Prevent blur
                  onClick={handleAddNewItem}
                >
                  <Icon name="add-outline" />
                  &nbsp;
                  {`${_t("add")} ${searchValue ? `"${searchValue}"` : _t("new").toLowerCase()}`}
                </Button>
              </div>
            )}
          </>
        )}
      >
        {isLoading ? (
          <Option key="init" value={"-10"} className="text-center" disabled={!mode}>
            <Spin spinning />
          </Option>
        ) : (
          optionData?.map((item) => (
            <Option key={item.id} value={mode ? String(item.id) : item.id} label={item.title}>
              {item.title || item.id}
            </Option>
          ))
        )}
      </Select>
      {isError && <Alert message={_t("msg.unknown_error")} className="mt-1" type="error" closable />}
    </>
  );
});

export default ServerSelectBox;
